import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CONSENT_KEY = 'correlationlab_cookie_consent';

export const CONSENT_CATEGORIES = {
  necessary: {
    id: 'necessary',
    name: 'Strictly Necessary',
    description: 'Essential for the website to function. Cannot be disabled.',
    required: true,
  },
  functional: {
    id: 'functional',
    name: 'Functional',
    description: 'Remember your preferences like dark mode and shopping cart.',
    required: false,
  },
  analytics: {
    id: 'analytics',
    name: 'Analytics',
    description: 'Help us understand how visitors interact with our website.',
    required: false,
  },
};

const DEFAULT_CONSENT = {
  necessary: true,
  functional: false,
  analytics: false,
  timestamp: null,
  version: '1.0',
};

const CookieConsentContext = createContext(null);

export function CookieConsentProvider({ children }) {
  const [consent, setConsent] = useState(null);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const savedConsent = localStorage.getItem(CONSENT_KEY);
    if (savedConsent) {
      try {
        const parsed = JSON.parse(savedConsent);
        setConsent(parsed);
        setShowBanner(false);
      } catch {
        setConsent(DEFAULT_CONSENT);
        setShowBanner(true);
      }
    } else {
      setConsent(DEFAULT_CONSENT);
      setShowBanner(true);
    }
  }, []);

  const saveConsent = useCallback((newConsent) => {
    const consentWithMeta = {
      ...newConsent,
      necessary: true,
      timestamp: new Date().toISOString(),
      version: '1.0',
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consentWithMeta));
    setConsent(consentWithMeta);
    setShowBanner(false);
  }, []);

  const acceptAll = useCallback(() => {
    saveConsent({
      necessary: true,
      functional: true,
      analytics: true,
    });
  }, [saveConsent]);

  const acceptNecessary = useCallback(() => {
    saveConsent({
      necessary: true,
      functional: false,
      analytics: false,
    });
  }, [saveConsent]);

  const acceptSelected = useCallback((selections) => {
    saveConsent({
      necessary: true,
      ...selections,
    });
  }, [saveConsent]);

  const hasConsent = useCallback((category) => {
    if (!consent) return category === 'necessary';
    return consent[category] === true;
  }, [consent]);

  const resetConsent = useCallback(() => {
    localStorage.removeItem(CONSENT_KEY);
    setConsent(DEFAULT_CONSENT);
    setShowBanner(true);
  }, []);

  return (
    <CookieConsentContext.Provider
      value={{
        consent,
        showBanner,
        setShowBanner,
        acceptAll,
        acceptNecessary,
        acceptSelected,
        hasConsent,
        resetConsent,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
}
